import { useEffect, useMemo, useRef, useState } from 'react';
import type cytoscape from 'cytoscape';
import { Network, Route } from 'lucide-react';

import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import type { EdgeRecord, RouteCandidate } from '@/features/network/models';
import { effectiveLatency } from '@/features/routing/solver';
import { formatCapacity, formatLatency } from '@/lib/format';
import { useLocalStorageState } from '@/lib/storage';

const GRAPH_NODE_POSITIONS_STORAGE_KEY = 'network-route-planner:graph-node-positions';

type NodePosition = {
  x: number;
  y: number;
};

type NodePositionMap = Record<string, NodePosition>;

const NODE_BASE_RING_RADIUS = 180;
const NODE_RING_GAP = 135;
const NODE_MIN_ARC_LENGTH = 72;
const COMPONENT_GAP = 520;
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));
const GRAPH_PADDING = 110;
const NODE_LABEL_MAX_LENGTH = 100;

function normalizeNodeLabel(label: string) {
  const trimmedLabel = label.trim();
  if (trimmedLabel.length <= NODE_LABEL_MAX_LENGTH) return trimmedLabel;
  return `${trimmedLabel.slice(0, NODE_LABEL_MAX_LENGTH - 3)}...`;
}

const INITIAL_LAYOUT_OPTIONS: cytoscape.LayoutOptions = {
  name: 'cose',
  fit: true,
  padding: GRAPH_PADDING,
  animate: false,
  randomize: false,
  nodeRepulsion: 95000,
  nodeOverlap: 24,
  idealEdgeLength: 120,
  edgeElasticity: 90,
  nestingFactor: 0.7,
  gravity: 0.22,
  numIter: 1200,
  initialTemp: 240,
  coolingFactor: 0.96,
  minTemp: 1,
};

function isEdgeElement(element: cytoscape.ElementDefinition) {
  return Boolean(element.data && typeof element.data === 'object' && 'source' in element.data);
}

function edgeHighlightKeys(route: RouteCandidate | null) {
  return new Set(route?.edgeKeys ?? []);
}

function makeEdgeKey(edge: EdgeRecord) {
  return `${edge.id}::${[edge.from, edge.to].sort().join(' ⇄ ')}`;
}

function sortedNodeIds(adjacency: Map<string, Set<string>>) {
  return Array.from(adjacency.keys()).sort((left, right) => left.localeCompare(right));
}

function pickHubNode(nodes: string[], adjacency: Map<string, Set<string>>) {
  return nodes.reduce((best, current) => {
    if (!best) return current;
    const bestDegree = adjacency.get(best)?.size ?? 0;
    const currentDegree = adjacency.get(current)?.size ?? 0;
    if (currentDegree !== bestDegree) return currentDegree > bestDegree ? current : best;
    return current.localeCompare(best) < 0 ? current : best;
  }, '' as string);
}

function collectConnectedComponents(adjacency: Map<string, Set<string>>) {
  const visited = new Set<string>();
  const components: string[][] = [];

  for (const start of sortedNodeIds(adjacency)) {
    if (visited.has(start)) continue;
    const stack = [start];
    const component: string[] = [];
    visited.add(start);

    while (stack.length > 0) {
      const node = stack.pop();
      if (!node) continue;
      component.push(node);

      const neighbors = Array.from(adjacency.get(node) ?? []).sort((left, right) => left.localeCompare(right));
      for (const neighbor of neighbors) {
        if (visited.has(neighbor)) continue;
        visited.add(neighbor);
        stack.push(neighbor);
      }
    }

    components.push(component.sort((left, right) => left.localeCompare(right)));
  }

  return components.sort((left, right) => {
    if (left.length !== right.length) return right.length - left.length;
    return left[0].localeCompare(right[0]);
  });
}

function buildInitialNodePositions(edges: EdgeRecord[]) {
  const adjacency = new Map<string, Set<string>>();

  for (const edge of edges) {
    if (!adjacency.has(edge.from)) adjacency.set(edge.from, new Set());
    if (!adjacency.has(edge.to)) adjacency.set(edge.to, new Set());
    adjacency.get(edge.from)?.add(edge.to);
    adjacency.get(edge.to)?.add(edge.from);
  }

  const components = collectConnectedComponents(adjacency);
  const positions: NodePositionMap = {};

  components.forEach((component, componentIndex) => {
    const componentRadius = componentIndex === 0 ? 0 : COMPONENT_GAP * Math.ceil(Math.sqrt(componentIndex));
    const componentAngle = componentIndex * GOLDEN_ANGLE;
    const centerX = Math.cos(componentAngle) * componentRadius;
    const centerY = Math.sin(componentAngle) * componentRadius;

    const hub = pickHubNode(component, adjacency);
    const queue = [hub];
    const distance = new Map<string, number>([[hub, 0]]);

    while (queue.length > 0) {
      const node = queue.shift();
      if (!node) continue;
      const currentDistance = distance.get(node) ?? 0;

      const neighbors = Array.from(adjacency.get(node) ?? []).sort((left, right) => left.localeCompare(right));
      for (const neighbor of neighbors) {
        if (!component.includes(neighbor) || distance.has(neighbor)) continue;
        distance.set(neighbor, currentDistance + 1);
        queue.push(neighbor);
      }
    }

    const layers = new Map<number, string[]>();
    for (const node of component) {
      const level = distance.get(node) ?? 0;
      if (!layers.has(level)) layers.set(level, []);
      layers.get(level)?.push(node);
    }

    const orderedLevels = Array.from(layers.keys()).sort((left, right) => left - right);
    for (const level of orderedLevels) {
      const layerNodes = layers.get(level) ?? [];
      layerNodes.sort((left, right) => {
        const leftDegree = adjacency.get(left)?.size ?? 0;
        const rightDegree = adjacency.get(right)?.size ?? 0;
        if (leftDegree !== rightDegree) return rightDegree - leftDegree;
        return left.localeCompare(right);
      });

      if (level === 0) {
        positions[layerNodes[0]] = { x: centerX, y: centerY };
        continue;
      }

      const count = layerNodes.length;
      const desiredRadius = NODE_BASE_RING_RADIUS + (level - 1) * NODE_RING_GAP;
      const minRadiusForArc = (count * NODE_MIN_ARC_LENGTH) / (2 * Math.PI);
      const radius = Math.max(desiredRadius, minRadiusForArc);
      const angleStep = (Math.PI * 2) / count;
      const angleOffset = componentAngle + level * 0.42;

      layerNodes.forEach((node, index) => {
        const angle = angleOffset + index * angleStep;
        positions[node] = {
          x: centerX + Math.cos(angle) * radius,
          y: centerY + Math.sin(angle) * radius,
        };
      });
    }
  });

  return positions;
}

export function GraphView({
  edges,
  primary,
  backup,
  layoutRerunVersion = 0,
}: {
  edges: EdgeRecord[];
  primary: RouteCandidate | null;
  backup: RouteCandidate | null;
  layoutRerunVersion?: number;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [selection, setSelection] = useState<{ title: string; body: string } | null>(null);
  const [storedPositions, setStoredPositions] = useLocalStorageState<NodePositionMap>(GRAPH_NODE_POSITIONS_STORAGE_KEY, {});
  const storedPositionsRef = useRef<NodePositionMap>(storedPositions);

  useEffect(() => {
    storedPositionsRef.current = storedPositions;
  }, [storedPositions]);

  const elements = useMemo<cytoscape.ElementDefinition[]>(() => {
    const nodeSet = new Set<string>();
    const primarySet = edgeHighlightKeys(primary);
    const backupSet = edgeHighlightKeys(backup);

    const edgeElements = edges.map((edge, index) => {
      nodeSet.add(edge.from);
      nodeSet.add(edge.to);
      const edgeKey = makeEdgeKey(edge);
      return {
        data: {
          id: `${edge.id}-${index}`,
          source: edge.from,
          target: edge.to,
          label: edge.subject || edge.id,
          latency: effectiveLatency(edge),
          capacity: edge.capacityMbps,
          vendor: edge.vendorName,
          quality: edge.quality,
          manualReview: edge.manualReview,
          isPrimary: primarySet.has(edgeKey),
          isBackup: backupSet.has(edgeKey),
        },
        classes: primarySet.has(edgeKey) ? 'edge-primary' : backupSet.has(edgeKey) ? 'edge-backup' : 'edge-default',
      };
    });

    const routeNodeSet = new Set([...(primary?.nodes ?? []), ...(backup?.nodes ?? [])]);
    const nodeElements = Array.from(nodeSet).map((node) => ({
      data: { id: node, label: normalizeNodeLabel(node) },
      classes: routeNodeSet.has(node) ? 'node-route' : 'node-default',
    }));

    return [...nodeElements, ...edgeElements];
  }, [edges, primary, backup]);

  const initialPositions = useMemo(() => buildInitialNodePositions(edges), [edges]);

  useEffect(() => {
    let cy: cytoscape.Core | null = null;
    let disposed = false;

    async function boot() {
      if (!containerRef.current) return;
      const cytoscapeModule = await import('cytoscape');
      if (disposed) return;
      const create = cytoscapeModule.default;

      const currentPositions = storedPositionsRef.current;
      const nodeIds = elements.filter((element) => !isEdgeElement(element)).map((element) => String(element.data?.id ?? ''));
      const shouldForceRelayout = layoutRerunVersion > 0;
      const hasCompleteSavedLayout = !shouldForceRelayout && nodeIds.length > 0 && nodeIds.every((nodeId) => Boolean(currentPositions[nodeId]));
      const runtimeElements = elements.map((element) => {
        if (isEdgeElement(element)) return element;
        const nodeId = String(element.data?.id ?? '');
        const position = hasCompleteSavedLayout ? currentPositions[nodeId] : initialPositions[nodeId];
        return position ? { ...element, position } : element;
      });

      cy = create({
        container: containerRef.current,
        elements: runtimeElements,
        layout: {
          name: 'preset',
          fit: true,
          padding: GRAPH_PADDING,
          animate: false,
        },
        style: [
          {
            selector: 'node',
            style: {
              label: 'data(label)',
              'font-size': 8,
              'text-wrap': 'wrap',
              'text-max-width': '90px',
              'min-zoomed-font-size': 9,
              'text-valign': 'bottom',
              'text-margin-y': 4,
              width: 12,
              height: 12,
              'background-color': '#94a3b8',
              color: '#334155',
            },
          },
          {
            selector: '.node-route',
            style: {
              'background-color': '#0ea5e9',
              width: 16,
              height: 16,
            },
          },
          {
            selector: 'edge',
            style: {
              width: 1.4,
              'curve-style': 'bezier',
              'line-color': '#cbd5e1',
              opacity: 0.45,
            },
          },
          {
            selector: '.edge-primary',
            style: {
              width: 3,
              'line-color': '#16a34a',
              opacity: 1,
            },
          },
          {
            selector: '.edge-backup',
            style: {
              width: 2.4,
              'line-color': '#f59e0b',
              opacity: 0.95,
            },
          },
        ],
      });

      cy.on('tap', 'node', (event) => {
        const label = event.target.data('label');
        setSelection({ title: label, body: 'Узел графа' });
      });

      cy.on('tap', 'edge', (event) => {
        const data = event.target.data();
        setSelection({
          title: data.label || `${data.source} → ${data.target}`,
          body: `${data.source} → ${data.target}\nLatency: ${formatLatency(data.latency ?? null)}\nCapacity: ${formatCapacity(data.capacity ?? null)}\nVendor: ${data.vendor || '—'}`,
        });
      });

      cy.on('dragfree', 'node', () => {
        if (!cy) return;
        const positions: NodePositionMap = {};
        cy.nodes().forEach((node) => {
          positions[node.id()] = node.position();
        });
        setStoredPositions((previous) => ({ ...previous, ...positions }));
      });

      if (!hasCompleteSavedLayout) {
        cy.layout(INITIAL_LAYOUT_OPTIONS).run();

        const positions: NodePositionMap = {};
        cy.nodes().forEach((node) => {
          positions[node.id()] = node.position();
        });
        setStoredPositions((previous) => ({ ...previous, ...positions }));
      }
    }

    boot();

    return () => {
      disposed = true;
      cy?.destroy();
    };
  }, [elements, initialPositions, layoutRerunVersion, setStoredPositions]);

  return (
    <Card className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-lg font-semibold">
            <Network className="h-5 w-5" />
            Карта сети
          </div>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            Визуализация подгруженного графа. Основной путь подсвечен зелёным, резервный — янтарным.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge><Route className="h-3.5 w-3.5" /> Узлов: {new Set(edges.flatMap((edge) => [edge.from, edge.to])).size}</Badge>
          <Badge>Рёбер: {edges.length}</Badge>
        </div>
      </div>

      <div className="mt-5 flex flex-col gap-4">
        <div className="rounded-3xl border border-slate-200 p-4 text-sm dark:border-slate-800">
          <div className="font-medium">Инспектор</div>
          {selection ? (
            <>
              <div className="mt-3 text-sm font-semibold text-slate-900 dark:text-slate-100">{selection.title}</div>
              <pre className="mt-3 whitespace-pre-wrap text-xs text-slate-500 dark:text-slate-400">{selection.body}</pre>
            </>
          ) : (
            <div className="mt-3 text-slate-500 dark:text-slate-400">Кликни по узлу или ребру, чтобы увидеть детали.</div>
          )}
        </div>
        <div ref={containerRef} className="h-[640px] rounded-3xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950" />
      </div>
    </Card>
  );
}
